export class Colors {
	/** Verde água do guia — logo, botão primário, seleção. Nunca como texto pequeno. */
	static primaryColor = '#108678';
	/** Verde água claro — só fundo de pílula. */
	static primarySoft = '#75C0B6';
	/** Bege — uma ação principal por tela. */
	static gold = '#B58B40';
	/** Azul do guia — underline, link e informação. */
	static info = '#2A7EB4';
	/** Limão — concluído e vigente. */
	static success = '#96C83C';
	static textCharcoal = '#1F2A28';
	/** Chão da página, cinza neutro. Cartões e tabela ficam brancos em cima. Sem tinta verde: o
	 * verde fica só nos elementos de marca, senão a tela inteira fica mentolada. */
	static canvas = '#F6F9F8';
	static cardBorder = '#E1E7E5';
	/** Tinta de seleção: verde água bem diluído, sem pintar o texto. */
	static primaryTint = 'rgba(16, 134, 120, 0.14)';
	static fontFamily = "'Outfit', 'Segoe UI', sans-serif";
	static mainBackgroundColor = '#1d1d1d';
	static softBackground = '#1e1f20';
	static strokeColor = '#292929';
	static darkCardBackground = 'rgb(20 20 20)';
	static lightCardBackground = '#fff';
	static textGreyTinted = '#565B64';
	static warning = '#FFC300';
	static danger = '#DC143C';

	/** Superfícies do tema escuro. Espelho de --alm-* em main.scss; o naive-ui lê daqui (dark.ts). */
	static dark = {
		canvas: '#101214',
		surface: '#1A1D20',
		surface2: '#23272C',
		border: '#353B43',
		text: '#EBECEF',
		textMuted: '#A1A9B3',
		textSoft: '#848C96',
		hover: '#282D33',
	};

	/**
	 * Tom suave a partir de uma cor cheia: fundo translúcido e texto forte. Devolve as duas
	 * variantes (clara e escura) como variáveis CSS; a classe `.alm-tone` em main.scss escolhe a
	 * do tema atual. Usar junto com `class="alm-tone"` na NTag.
	 */
	static softTone(hex: string) {
		const { r, g, b } = Colors.hexToRgb(hex);
		return {
			'--alm-tone-bg-light': `rgba(${r}, ${g}, ${b}, 0.20)`,
			'--alm-tone-light': Colors.lightenDarkenColor(hex, -52),
			'--alm-tone-bg-dark': `rgba(${r}, ${g}, ${b}, 0.28)`,
			'--alm-tone-dark': Colors.lightenDarkenColor(hex, 58),
		};
	}

	static naiveBrand() {
		const tone = (hex: string) => ({
			base: hex,
			hover: Colors.lightenDarkenColor(hex, 14),
			pressed: Colors.lightenDarkenColor(hex, -18),
		});
		const primary = tone(Colors.primaryColor);
		const info = tone(Colors.info);
		// Sucesso na família do verde água. O limão do guia é bloco sólido; como texto azeda.
		const success = tone(Colors.primaryColor);
		return {
			primaryColor: primary.base,
			primaryColorHover: primary.hover,
			primaryColorPressed: primary.pressed,
			primaryColorSuppl: primary.base,
			infoColor: info.base,
			infoColorHover: info.hover,
			infoColorPressed: info.pressed,
			infoColorSuppl: info.base,
			successColor: success.base,
			successColorHover: success.hover,
			successColorPressed: success.pressed,
			successColorSuppl: success.base,
			textColorBase: Colors.textCharcoal,
			fontFamily: Colors.fontFamily,
			borderRadius: '10px',
			borderRadiusSmall: '8px',
		};
	}

	static getHash(text: string) {
		let hash = 0;
		if (text.length == 0) {
			return hash;
		}
		for (let i = 0; i < text.length; i++) {
			const charCode = text.charCodeAt(i);
			hash = (hash << 5) - hash + charCode;
			hash = hash & hash;
		}
		return hash;
	}

	static generateColor(username: string, startColor = [201, 174, 93], endColor = [26, 34, 63]) {
		const colorRange = Colors.interpolateColors(startColor, endColor, 3);
		const colorIndex = Math.abs(Colors.getHash(username) % 3);
		const color = `#${Colors.rgbToHex(colorRange[colorIndex])}`;
		const textColor = Colors.calculateTextColor(color);

		return {
			background: color,
			color: textColor,
		};
	}

	static interpolateColors(startColor: number[], endColor: number[], steps: number) {
		const stepFactor = 1 / (steps - 1);
		const interpolatedColors = [];
		for (let i = 0; i < steps; i++) {
			const color = [];
			for (let j = 0; j < 3; j++) {
				color.push(Math.round(startColor[j] + (endColor[j] - startColor[j]) * i * stepFactor));
			}
			interpolatedColors.push(color);
		}
		return interpolatedColors;
	}

	static rgbToHex(color: number[]) {
		const hex = color.map((c) => {
			const hexComponent = c.toString(16);
			return hexComponent.length == 1 ? '0' + hexComponent : hexComponent;
		});
		return hex.join('');
	}

	static calculateTextColor(backgroundColor: string) {
		// Converte a cor de fundo em um objeto RGB
		const rgb = Colors.hexToRgb(backgroundColor);
		// Calcula a luminosidade do fundo
		const luminance = Colors.calculateLuminance(rgb);
		// Retorna uma cor de texto preto ou branco, dependendo da luminosidade do fundo
		return luminance > 0.5 ? '#000' : '#fff';
	}

	static hexToRgb(hex: string) {
		const r = parseInt(hex.substring(1, 3), 16);
		const g = parseInt(hex.substring(3, 5), 16);
		const b = parseInt(hex.substring(5, 7), 16);
		return { r, g, b };
	}

	static hexToRgbString(hex: string) {
		const result = Colors.hexToRgb(hex);

		return `${result.r}, ${result.g}, ${result.b}`;
	}

	static calculateLuminance(rgb: { r: number; g: number; b: number }) {
		const a = [rgb.r, rgb.g, rgb.b].map(function (v) {
			v /= 255;
			return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
		});
		return 0.2126 * a[0] + 0.7152 * a[1] + 0.0722 * a[2];
	}

	static lightenDarkenColor(col: string, amt: number) {
		col = col.replace(/^#/, '');
		if (col.length === 3) col = col[0] + col[0] + col[1] + col[1] + col[2] + col[2];

		//@ts-expect-error problema de type
		let [r, g, b] = col.match(/.{2}/g);
		[r, g, b] = [parseInt(r, 16) + amt, parseInt(g, 16) + amt, parseInt(b, 16) + amt];

		r = Math.max(Math.min(255, r), 0).toString(16);
		g = Math.max(Math.min(255, g), 0).toString(16);
		b = Math.max(Math.min(255, b), 0).toString(16);

		const rr = (r.length < 2 ? '0' : '') + r;
		const gg = (g.length < 2 ? '0' : '') + g;
		const bb = (b.length < 2 ? '0' : '') + b;

		return `#${rr}${gg}${bb}`;
	}
}

export default Colors;
